import { ShieldAlert, Lock, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";

export function ImpersonationBanner({ user, organization, onExit }) {
  const { t } = useTranslation("common");
  if (!user?.isImpersonated) return null;

  return (
    <div
      role="banner"
      aria-label={t("impersonation.activeAria", "Diagnostic Impersonation Active")}
      className="relative z-30 w-full bg-warning text-warning-foreground px-3 sm:px-6 py-2 shadow-sm border-b border-warning/40 flex flex-wrap items-center justify-between gap-2 text-xs select-none"
    >
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold bg-warning-foreground/15 text-warning-foreground border border-warning-foreground/20 text-caption-size tracking-wide uppercase shrink-0">
          <ShieldAlert className="h-3.5 w-3.5 animate-pulse text-warning-foreground" />
          <span>{t("impersonation.badgeTitle", "Diagnostic Impersonation")}</span>
        </div>

        <div className="flex items-center gap-1.5 truncate">
          <span className="text-warning-foreground/80">{t("impersonation.viewingAs", "Viewing as")}</span>
          <strong className="font-semibold text-warning-foreground underline decoration-warning-foreground/40 underline-offset-2">
            {user.fullName || user.email}
          </strong>
          <span className="text-caption-size font-mono px-1 rounded bg-warning-foreground/20 text-warning-foreground font-semibold">
            {user.role}
          </span>
          {(organization?.name || user.organizationSlug) && (
            <span className="hidden md:inline text-warning-foreground/80">
              {t("impersonation.in", "in")} <strong>{organization?.name || user.organizationSlug}</strong>
            </span>
          )}
        </div>

        {user.isReadOnly ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold bg-warning-foreground text-warning text-overline-size tracking-wider uppercase shrink-0 shadow-xs">
            <Lock className="h-3 w-3" />
            {t("impersonation.readOnlyGuard", "Read-Only Guard Active")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold bg-success text-success-foreground text-overline-size tracking-wider uppercase shrink-0">
            {t("impersonation.writeAllowed", "Write Allowed")}
          </span>
        )}

        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold bg-warning-foreground/15 text-warning-foreground text-overline-size tracking-wider uppercase shrink-0 border border-warning-foreground/20">
          {t("impersonation.ttl", "30m TTL")}
        </span>

        {user.impersonatorEmail && (
          <span className="hidden lg:inline text-caption-size text-warning-foreground/85">
            {t("impersonation.operator", "Operator:")} <span className="font-medium underline decoration-dotted">{user.impersonatorEmail}</span>
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onExit}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg font-medium text-xs bg-warning-foreground text-warning hover:bg-warning-foreground/90 active:scale-95 transition-all shadow-sm cursor-pointer min-h-[44px] sm:min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-warning"
          title={t("impersonation.endSession", "End Diagnostic Session")}
        >
          <LogOut className="h-3.5 w-3.5" />
          <span>{t("impersonation.exit", "Exit Impersonation")}</span>
        </button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
